import { Flex, Text } from "@chakra-ui/layout";
import { Grid } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FormikContextType, useField, useFormikContext } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { addressLookup } from "src/components/Boundary/services";
import { AddressAutocomplete } from "src/components/Inputs/Address/Autocomplete/AddressAutocompleteInput";
import { InputWithValidationControl } from "src/components/Inputs/InputWithValidationControl";
import {
  AddressValidationSchema,
  formatZipCode,
  PersonAddress,
} from "src/schemas/Address";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";

export type Props = {
  isRequired?: boolean;
  isDisabled?: boolean;
  fieldPrefix?: string;
  autoFocus?: boolean;
};
type Geom = {
  type: "Point";
  coordinates?: [number, number] | null;
};

function decimalToDMS(decimal: number, isLat: boolean): string {
  const abs = Math.abs(decimal);
  const degrees = Math.floor(abs);
  const minutesDecimal = (abs - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = (minutesDecimal - minutes) * 60;
  const dir = isLat ? (decimal >= 0 ? "N" : "S") : decimal >= 0 ? "E" : "W";
  return `${degrees}°${minutes}'${seconds.toFixed(1)}"${dir}`;
}

function googleMapsPlaceUrl(lat: number, lng: number): string {
  const placeName = `${decimalToDMS(lat, true)} ${decimalToDMS(lng, false)}`;
  const encoded = encodeURIComponent(placeName).replace(/%20/g, "+");
  return `https://www.google.com/maps/place/${encoded}/@${lat},${lng}`;
}

type AddressFormValues = {
  street_address: string;
  street_address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  geom: Geom | null;
};

function toAddressFormValues(
  source?: Partial<AddressFormValues>
): AddressFormValues {
  return {
    street_address: source?.street_address ?? "",
    street_address_line_2: source?.street_address_line_2 ?? "",
    city: source?.city ?? "",
    state: source?.state ?? "",
    zip_code: formatZipCode(source?.zip_code ?? ""),
    geom: source?.geom ?? null,
  };
}

function addressKey(address: AddressFormValues) {
  return [
    address.street_address,
    address.street_address_line_2,
    address.city,
    address.state,
    address.zip_code,
  ].join("\0");
}

class AddressGeocodeError extends Error {
  constructor(readonly reason: "missing" | "failed") {
    super(reason);
  }
}

function useFormAddressGeocode(
  address: AddressFormValues,
  { enabled }: { enabled: boolean }
) {
  const query = useQuery({
    queryKey: ["formAddressGeocode", addressKey(address)],
    enabled,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async (): Promise<[number, number]> => {
      try {
        const coordinates = await addressLookup(new google.maps.Geocoder(), {
          street_address: address.street_address,
          street_address_line_2: address.street_address_line_2,
          city: address.city,
          state: address.state,
          zip_code: address.zip_code,
        });
        const lng = coordinates?.[0];
        const lat = coordinates?.[1];
        if (lng === undefined || lat === undefined) {
          throw new AddressGeocodeError("missing");
        }
        return [lng, lat];
      } catch (error) {
        if (error instanceof AddressGeocodeError) throw error;
        throw new AddressGeocodeError("failed");
      }
    },
  });

  const error =
    query.isError && !query.isFetching
      ? query.error instanceof AddressGeocodeError
        ? query.error.reason
        : "failed"
      : null;

  return {
    coordinates: query.data,
    retry: query.refetch,
    error,
  };
}

const Coordinates: React.FC<{
  withPrefix: (name: string) => string;
  formik: FormikContextType<Record<string, unknown>>;
  geom: Geom | null;
  showLatLngForm: boolean;
  hideCoordinateWarning: boolean;
  onClickAcceptEnterCoordinates: () => void;
  onRetryGeocode: () => Promise<void>;
  hasFailedGeocode: boolean;
}> = ({
  withPrefix,
  formik,
  geom,
  showLatLngForm,
  hideCoordinateWarning,
  onClickAcceptEnterCoordinates,
  onRetryGeocode,
  hasFailedGeocode,
}) => {
  const [latValue, setLatValue] = useState<string>("");
  const [lngValue, setLngValue] = useState<string>("");

  useEffect(() => {
    if (!showLatLngForm) {
      setLatValue("");
      setLngValue("");
    }
  }, [showLatLngForm]);

  return (
    <Flex direction={"column"} width="100%">
      {geom?.coordinates && !showLatLngForm && (
        <Text color={"gray.600"} fontSize={"sm"}>
          Coordinates used for Boundary eligibility:{" "}
          <Text
            as="a"
            href={googleMapsPlaceUrl(geom.coordinates[1], geom.coordinates[0])}
            target="_blank"
            rel="noopener noreferrer"
            color="blue.500"
            textDecoration="underline"
            cursor="pointer"
            _hover={{ color: "blue.600" }}
          >
            {`${geom.coordinates[1].toFixed(4)}, ${geom.coordinates[0].toFixed(
              4
            )}`}
          </Text>
        </Text>
      )}
      {!geom?.coordinates && !showLatLngForm && !hideCoordinateWarning && (
        <Text color={"red.500"} fontSize={"sm"}>
          {!hasFailedGeocode ? (
            <>
              {"Coordinates not found. Review address and "}
              <Text
                fontSize={"sm"}
                as="span"
                textDecoration="underline"
                cursor="pointer"
                _hover={{ color: "red.600" }}
                onClick={onRetryGeocode}
              >
                retry geocoding
              </Text>
              {" or "}
              <Text
                fontSize={"sm"}
                as="span"
                textDecoration="underline"
                cursor="pointer"
                _hover={{ color: "red.600" }}
                onClick={onClickAcceptEnterCoordinates}
              >
                enter coordinates manually
              </Text>
              {" to support Boundary eligibility for this address."}
            </>
          ) : (
            <>
              Cannot geocode address, please{" "}
              <Text
                fontSize={"sm"}
                as="span"
                textDecoration="underline"
                cursor="pointer"
                _hover={{ color: "red.600" }}
                onClick={onClickAcceptEnterCoordinates}
              >
                enter coordinates manually
              </Text>
              {" to support Boundary eligibility for this address."}
            </>
          )}
        </Text>
      )}
      {showLatLngForm && !hideCoordinateWarning && (
        <Text color={"gray.600"} fontSize={"sm"}>
          Coordinates not found, manually enter them. Find in{" "}
          <Text
            as="a"
            href="https://maps.google.com"
            target="_blank"
            rel="noopener noreferrer"
            color="blue.500"
            textDecoration="underline"
            cursor="pointer"
            _hover={{ color: "blue.600" }}
          >
            Google Maps
          </Text>
          .
        </Text>
      )}
      {showLatLngForm && (
        <Flex gap={3} marginTop="1rem">
          <InputWithValidationControl
            isRequired={false}
            label="Latitude"
            isInvalid={Number.isNaN(parseFloat(latValue))}
            inputProps={{
              placeholder: "E.g.: 32.69848346626098",
              value: latValue,
              onChange: (e) => {
                const value = e.target.value;
                setLatValue(value);
                const lat = parseFloat(value);
                const lng = parseFloat(lngValue);
                if (!isNaN(lat) && !isNaN(lng)) {
                  formik.setFieldValue(withPrefix("geom"), {
                    coordinates: [lng, lat],
                  });
                } else {
                  formik.setFieldValue(withPrefix("geom"), null);
                }
              },
            }}
            name={withPrefix("latitude")}
            id="latitude"
          />
          <InputWithValidationControl
            isRequired={false}
            label="Longitude"
            isInvalid={Number.isNaN(parseFloat(lngValue))}
            inputProps={{
              placeholder: "E.g.: -117.11873953103859",
              value: lngValue,
              onChange: (e) => {
                const value = e.target.value;
                setLngValue(value);
                const lng = parseFloat(value);
                const lat = parseFloat(latValue);
                if (!isNaN(lng) && !isNaN(lat)) {
                  formik.setFieldValue(withPrefix("geom"), {
                    coordinates: [lng, lat],
                  });
                } else {
                  formik.setFieldValue(withPrefix("geom"), null);
                }
              },
            }}
            name={withPrefix("longitude")}
            id="longitude"
          />
        </Flex>
      )}
    </Flex>
  );
};

export const FormAddressForm: React.FC<Props> = (props) => {
  const { isRequired, isDisabled, fieldPrefix, autoFocus } = props;
  const formik = useFormikContext<Record<string, unknown>>();
  const [showLatLngForm, setShowLatLngForm] = useState<boolean>(false);
  const withPrefix = useCallback(
    (name: string) => (fieldPrefix ? [fieldPrefix, name].join(".") : name),
    [fieldPrefix]
  );

  const [geom] = useField<Geom | null>(withPrefix("geom"));
  const [streetField] = useField<string>(withPrefix("street_address"));
  const [street2Field] = useField<string>(withPrefix("street_address_line_2"));
  const [cityField] = useField<string>(withPrefix("city"));
  const [stateField] = useField<string>(withPrefix("state"));
  const [zipField, , zipHelpers] = useField<string>(withPrefix("zip_code"));

  const address = toAddressFormValues({
    street_address: streetField.value,
    street_address_line_2: street2Field.value,
    city: cityField.value,
    state: stateField.value,
    zip_code: zipField.value,
  });
  const [committedAddress, setCommittedAddress] = useState(address);
  const addressChanged = addressKey(address) !== addressKey(committedAddress);
  const keepSelectionGeom = useRef(false);
  const addressIsValid = AddressValidationSchema.safeParse(address).success;

  const {
    coordinates,
    retry: retryGeocode,
    error: geocodeError,
  } = useFormAddressGeocode(committedAddress, {
    enabled: !addressChanged && addressIsValid && !geom.value?.coordinates,
  });

  useEffect(() => {
    // Places selection writes geom itself.
    if (keepSelectionGeom.current) {
      keepSelectionGeom.current = false;
      return;
    }
    if (addressChanged) {
      formik.setFieldValue(withPrefix("geom"), null);
      setShowLatLngForm(false);
      return;
    }
    if (!coordinates || geom.value?.coordinates) return;
    formik.setFieldValue(withPrefix("geom"), {
      type: "Point",
      coordinates,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressChanged, coordinates, geom.value, withPrefix]);

  const setAddressFields = (selected: PersonAddress) => {
    keepSelectionGeom.current = true;
    for (const [key, value] of Object.entries(selected)) {
      formik.setFieldValue(withPrefix(key), value ?? "", false);
    }

    setCommittedAddress(toAddressFormValues(selected));
    setShowLatLngForm(false);
    setTimeout(() => {
      formik.validateForm();
    });
  };

  const handleAddressSectionBlur = (
    event: React.FocusEvent<HTMLDivElement>
  ) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;

    const placesDropdownOpen = Array.from(
      document.querySelectorAll<HTMLElement>(".pac-container")
    ).some((el) => el.offsetParent !== null);
    if (placesDropdownOpen) return;

    if (addressChanged) setCommittedAddress(address);
  };

  return (
    <>
      <Flex direction="column" width="100%" onBlur={handleAddressSectionBlur}>
        <Grid
          gridTemplateColumns="repeat(auto-fit, minmax(15rem, 1fr))"
          alignItems="flex-end"
          gap={{ md: 3 }}
        >
          <AddressAutocomplete onAddressSelected={setAddressFields}>
            <InputWithValidationControl
              isRequired={isRequired}
              label="Address"
              inputProps={
                {
                  placeholder: "E.g.: 532 Oakland Ave",
                  autoFocus,
                  autoComplete: "off",
                  ...PASSWORD_MANAGER_IGNORE,
                } as React.ComponentProps<
                  typeof InputWithValidationControl
                >["inputProps"]
              }
              name={withPrefix("street_address")}
              id="street_address"
              isDisabled={isDisabled}
            />
          </AddressAutocomplete>
          <InputWithValidationControl
            isRequired={false}
            inputProps={
              {
                placeholder: "E.g.: 5B",
                autoComplete: "off",
                ...PASSWORD_MANAGER_IGNORE,
              } as React.ComponentProps<
                typeof InputWithValidationControl
              >["inputProps"]
            }
            name={withPrefix("street_address_line_2")}
            id="street_address_line_2"
            label="Apartment/suite (Optional)"
            isDisabled={isDisabled}
          />
          <InputWithValidationControl
            isRequired={isRequired}
            inputProps={
              {
                placeholder: "E.g.: New Orleans",
                autoComplete: "off",
                ...PASSWORD_MANAGER_IGNORE,
              } as React.ComponentProps<
                typeof InputWithValidationControl
              >["inputProps"]
            }
            name={withPrefix("city")}
            id="city"
            label="City"
            isDisabled={isDisabled}
          />
          <Flex gap={3} alignItems="flex-end">
            <InputWithValidationControl
              isRequired={isRequired}
              inputProps={
                {
                  placeholder: "E.g.: LA",
                  autoComplete: "off",
                  ...PASSWORD_MANAGER_IGNORE,
                } as React.ComponentProps<
                  typeof InputWithValidationControl
                >["inputProps"]
              }
              name={withPrefix("state")}
              id="state"
              label="State"
              isDisabled={isDisabled}
            />
            <InputWithValidationControl
              isRequired={isRequired}
              inputProps={
                {
                  placeholder: "E.g.: 10181 or 10181-6789",
                  autoComplete: "off",
                  ...PASSWORD_MANAGER_IGNORE,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    zipHelpers.setValue(formatZipCode(e.target.value)),
                } as React.ComponentProps<
                  typeof InputWithValidationControl
                >["inputProps"]
              }
              name={withPrefix("zip_code")}
              id="zip_code"
              label="Zip code"
              isDisabled={isDisabled}
            />
          </Flex>
        </Grid>
      </Flex>
      <Flex gap={3} alignItems="flex-end">
        <Coordinates
          withPrefix={withPrefix}
          formik={formik}
          geom={geom.value ?? null}
          showLatLngForm={showLatLngForm}
          hideCoordinateWarning={addressChanged || geocodeError == null}
          onClickAcceptEnterCoordinates={() => setShowLatLngForm(true)}
          onRetryGeocode={async () => {
            setShowLatLngForm(false);
            await retryGeocode();
          }}
          hasFailedGeocode={geocodeError === "failed"}
        />
      </Flex>
    </>
  );
};
